// @givmo/board — the director's meeting list and one meeting, read-only, plus the RSVP.
//
// An RSVP is a READ-CLASS act: the account's sign-in is enough (derivation §1.6), with no signing
// window. Only signature-class acts — the minutes' signature on this page's minutes panel, a consent,
// an acknowledgment, a disclosure — run through the shared "Confirm it's you" sheet.
//
// ONCE A MEETING HAS STARTED (CBG-FIX25: its start is known and is at or before now) nobody is asked
// whether they can make it: no RSVP card, no calendar file (it answers the same question), no
// "Scheduled" tag, no "Waiting on you", and no answer badge either. The answer's words are in the
// present tense ("You're going"), and the RSVP card says an answer isn't a record of who actually
// attended; after the start, the minutes are. "Now" is the director pages' own clock, re-read at
// the start itself. A meeting called off keeps its "Cancelled" tag and, started or not, asks nobody
// either: no answer badge and no "Waiting on you", since the backend takes no answer to it.
//
// The notice block is the same backend-rendered sentence the officer sees, and a director is entitled
// to it for exactly the reason it exists: it is the organization's own recorded parameter and the
// arithmetic against it, with the citation, which is what someone called to a meeting can check for
// themselves.
//
// WHERE ONE SEAT LANDS (CBG-FIX109, frames b1–b5 and c1). The list of a board's meetings is the
// page someone holding one seat lands on, from sign-in and from every email that links home. What
// waits on them is on their to-do list, beside every page of the board (CBG-UX7-WEB-DIR, D-CBG-35
// (2): the "Waiting on you" card that sat at this page's top became the list, its words and Open
// carried over, and the list opens on its own when something new waits, so an emailed link still
// shows its item). Its "All your boards" (or "All your committees") would lead to a list of one, so
// it isn't there for the board home lands on (`all-your-boards.tsx`).
//
// THE OFFICER'S MEETINGS, A DIRECTOR'S ACTS (CBG-UX10, D-CBG-40; frames c1, c2, c7). The list is the
// officer page's: "Upcoming" and "Past" in one card each, every row its title, its day and its place,
// read from every meeting the seat may see (`upcoming_only=false`). In place of the officer's
// "Schedule a meeting" and "Write minutes", a meeting ahead carries the director's own acts: their
// answer, or "Can you make it?" to the meeting page where they give it, and "Open the packet" once
// its papers are out. "How meetings work" opens the board's recorded meeting rules (About the
// board's read). The empty states say what will appear, and when.

import { useId, useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { Link, useParams } from '../../board-routing';
import { extractList } from '@givmo/api-client/list';
import type { BoardAttendanceIntentValue, OrgBoardDirectorMeetingOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  Skeleton,
  saasButtonVariants,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import { GroupLabel, HowThisWorks, StatusWord } from '../../components/fine-print';
import {
  MEETINGS_TITLE,
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { AllYourBoards } from './all-your-boards';
import { DirectorBoardContent } from './director-board-content';
import { DirectorReadFailed, useDirectorFailureSentence } from '../../director/access-refusal';
import { directorPagesWords as w } from './director-pages-words';
import { DirectorShell } from './director-shell';
import { DirectorMinutesPanel } from './director-minutes-panel';
import { useMinuteClock } from '../../lib/minute-clock';
import { NoticeBlock } from '../meetings/notice-block';
import { saveCalendarFile } from '../meetings/calendar-download';
import { decodeDirectorMeetingCalendar } from '../meetings/calendar-decode';
import { calendarWords } from '../../calendar/calendar-words';
import {
  directorIntentLabel,
  displayMeetingStart,
  meetingCommitteeName,
  meetingHasStarted,
  meetingTypeLabel,
  meetingWhen,
  statusLabel,
  statusTone,
} from '../meetings/meetings-decode';
import { keepRangesTogether } from '../meetings/keep-ranges-together';
import { extractAbout } from '../onboarding/onboarding-decode';
import { parameterLabel } from '../parameters/parameters-decode';

const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** A director's act beside a meeting's row: the officer row's "Write minutes" recipe. */
const SMALL_BUTTON = saasButtonVariants({
  size: 'sm',
  tone: 'secondary',
  className: 'no-underline',
});

const RSVP_CHOICES: readonly { value: BoardAttendanceIntentValue; label: string }[] = [
  { value: 'attending', label: 'I’ll be there' },
  { value: 'tentative', label: 'Not sure yet' },
  { value: 'not_attending', label: 'I can’t make it' },
];

function extractDirectorMeetings(body: unknown): OrgBoardDirectorMeetingOut[] {
  return extractList<OrgBoardDirectorMeetingOut>(body, 'data');
}

/**
 * The board's meetings the seat may see: every one (Meetings' Past), and beneath it the ones ahead
 * (Meetings' Upcoming, Committees' next meeting), so whatever refreshes one refreshes both.
 */
export function directorMeetingsKey(orgRef: string | null, which: 'all' | 'ahead' = 'all') {
  return which === 'all'
    ? (['board-director-meetings', orgRef] as const)
    : (['board-director-meetings', orgRef, 'ahead'] as const);
}

/**
 * The meetings the seat may see, soonest first, a hundred to a page as on the officer's page: every
 * one, the past ones too (`all`; the read's default is the ones ahead), or the ones ahead that stand
 * (`ahead`). The full read ends a board past a hundred meetings before the ones ahead, so what is
 * ahead is read on its own and never lost (review-291's F2).
 */
export function useDirectorMeetings(
  orgRef: string | null,
  enabled: boolean,
  which: 'all' | 'ahead',
) {
  const operations = useBoardDirectorOperations();
  return useQuery({
    queryKey: directorMeetingsKey(orgRef, which),
    enabled: orgRef !== null && enabled,
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorMeetingOut[]> => {
      const { data } = await operations.directorListBoardDirectorMeetings({
        path: { org_ref: orgRef ?? '' },
        query: { upcoming_only: which === 'ahead', 'page[limit]': 100 },
        throwOnError: true,
      });
      return extractDirectorMeetings(data);
    },
  });
}

/** When a meeting starts, for ordering: one with no start yet sorts last. */
function startOrder(meeting: OrgBoardDirectorMeetingOut): number {
  const at = meeting.starts_at ? Date.parse(meeting.starts_at) : Number.NaN;
  return Number.isNaN(at) ? Number.POSITIVE_INFINITY : at;
}

export function DirectorMeetingsPage() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, scope, me, settled } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);
  // CBG-YR6-OUTSIDE (frame o8): a seat for someone who isn't on the board reads its committees'
  // meetings alone (the backend scopes the list), named here as theirs; About the board, and so its
  // meeting rules, is the board's alone.
  const outside = scope.committeeOnly;
  const committees = scope.committees;

  const meetingsQuery = useDirectorMeetings(orgRef, mayFire, 'all');
  const aheadQuery = useDirectorMeetings(orgRef, mayFire, 'ahead');
  const reads = [meetingsQuery, aheadQuery];
  const failedRead = reads.find((read) => read.isError && !isSigningRefusal(read.error));
  const readsSettled = meetingsQuery.isSuccess && aheadQuery.isSuccess;
  // The board's recorded meeting rules behind "How meetings work", from About the board's read.
  const aboutQuery = useQuery({
    queryKey: ['board-director-about', orgRef ?? ''],
    enabled: orgRef !== null && mayFire && !outside && !notYours,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardAbout({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractAbout(data);
    },
  });
  const rules = (aboutQuery.data?.rules ?? []).map(
    (rule) =>
      `${parameterLabel(rule)}: ${rule.value_summary}${rule.bylaw_citation ? ` (${rule.bylaw_citation})` : ''}`,
  );

  const now = useMinuteClock(null);
  const meetings = meetingsQuery.data ?? [];
  // The officer page's two groups: what hasn't started, soonest first, and what has, latest first.
  // Upcoming is the read of the meetings ahead, with any the full read holds that it doesn't (one
  // called off still shows, as on the officer's page); Past is the full read's.
  const ahead = aheadQuery.data ?? [];
  const aheadIds = new Set(ahead.map((meeting) => meeting.id));
  const upcoming = [...ahead, ...meetings.filter((meeting) => !aheadIds.has(meeting.id))]
    .filter((meeting) => !meetingHasStarted(meeting.starts_at, now))
    .sort((left, right) => startOrder(left) - startOrder(right) || left.id.localeCompare(right.id));
  const past = meetings.filter((meeting) => meetingHasStarted(meeting.starts_at, now)).reverse();
  // The papers of a meeting ahead are on its own read (`published_packet_id`), under the meeting
  // page's key; a meeting called off has none to open.
  const answering = upcoming.filter((meeting) => meeting.status === 'scheduled');
  const meetingReads = useQueries({
    queries: answering.map((meeting) => ({
      queryKey: ['board-director-meeting', orgRef, meeting.id],
      enabled: orgRef !== null && mayFire,
      retry: false,
      queryFn: async (): Promise<OrgBoardDirectorMeetingOut> => {
        const { data } = await operations.directorGetBoardDirectorMeeting({
          path: { org_ref: orgRef ?? '', meeting_id: meeting.id },
          throwOnError: true,
        });
        return data;
      },
    })),
  });
  const packetOf = (meetingId: string): string | null => {
    const index = answering.findIndex((meeting) => meeting.id === meetingId);
    return index < 0 ? null : (meetingReads[index]?.data?.published_packet_id ?? null);
  };
  // Removed before finishing (frame o8): for a seat on one committee, which the description names,
  // not on every row.
  const committeeSaid = !(outside && committees.length === 1);

  return (
    <DirectorShell
      title={MEETINGS_TITLE}
      description={
        outside ? (
          co.meetingsDescription(committees)
        ) : (
          <>
            {w.meetingsLine}
            {rules.length > 0 ? (
              <>
                {' '}
                <HowThisWorks
                  testId="director-meetings-how"
                  label={w.howMeetingsWork}
                  lines={rules}
                />
              </>
            ) : null}
          </>
        )
      }
      identity={directorBoardIdentity(board)}
      actions={
        <AllYourBoards
          me={me}
          settled={settled}
          committeeOnly={outside}
          testId="director-meetings-back"
        />
      }
      testId="page-director-meetings"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {meetingsQuery.isPending || aheadQuery.isPending ? (
          <Skeleton className="h-84 w-full rounded-lg" testId="director-meetings-loading" />
        ) : null}

        {/* A window that shuts mid-visit refuses this read on a board that asks before anything
            opens; the gate opens the sheet and re-reads, rather than showing a generic failure. */}
        <DirectorReadRefusal reads={reads} testId="director-meetings-read-step-up" />

        {failedRead !== undefined ? (
          <DirectorReadFailed
            read={failedRead}
            line="We couldn’t load your meetings just now."
            testId="director-meetings-error"
            retryTestId="director-meetings-retry"
          />
        ) : null}

        {readsSettled && meetings.length === 0 && upcoming.length === 0 ? (
          // The officer's empty state, in a director's words: what will appear, and when.
          <EmptyState
            testId="director-meetings-empty"
            icon={<Icon icon={CalendarDays} size={20} aria-hidden />}
            // A committee seat's own line (approved) says it whole.
            title={outside ? co.meetingsEmpty : w.noMeetingsTitle}
            description={outside ? undefined : w.noMeetingsLine}
          />
        ) : null}

        {readsSettled && (meetings.length > 0 || upcoming.length > 0) ? (
          <section className="flex flex-col gap-10" data-testid="director-meetings-upcoming">
            <GroupLabel>{w.upcoming}</GroupLabel>
            {upcoming.length === 0 ? (
              <Card testId="director-meetings-nothing-ahead">
                <CardBody className="flex flex-col gap-2">
                  <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                    {outside ? co.meetingsEmpty : w.nothingAheadTitle}
                  </span>
                  {outside ? null : (
                    <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {w.nothingAheadLine}
                    </span>
                  )}
                </CardBody>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <ul className="divide-y divide-border-subtle">
                  {upcoming.map((meeting) => (
                    <MeetingRow
                      key={meeting.id}
                      meeting={meeting}
                      search={search}
                      committeeSaid={committeeSaid}
                      packetId={packetOf(meeting.id)}
                      asks
                    />
                  ))}
                </ul>
              </Card>
            )}
          </section>
        ) : null}

        {readsSettled && past.length > 0 ? (
          <section className="flex flex-col gap-10" data-testid="director-meetings-past">
            <GroupLabel>{w.past}</GroupLabel>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-border-subtle">
                {past.map((meeting) => (
                  <MeetingRow
                    key={meeting.id}
                    meeting={meeting}
                    search={search}
                    committeeSaid={committeeSaid}
                    packetId={null}
                    asks={false}
                  />
                ))}
              </ul>
            </Card>
          </section>
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

/**
 * The officer's meeting row (`meetings-page.tsx`): the title and the day at the top, the place (or
 * "Cancelled") under it, a committee's meeting leading with its committee. Its acts are the
 * director's, beside the row's link and never inside it — at its right from `sm`, under its lines on
 * a phone: on a meeting ahead, the answer ("You’re going", with "Change" beside it) or "Can you make
 * it?", and "Open the packet" once its papers are out (`asks`). Once a meeting has started nobody is
 * asked (CBG-FIX25).
 */
function MeetingRow({
  meeting,
  search,
  committeeSaid,
  packetId,
  asks,
}: {
  meeting: OrgBoardDirectorMeetingOut;
  search: Record<string, string> | undefined;
  committeeSaid: boolean;
  packetId: string | null;
  asks: boolean;
}) {
  const committee = committeeSaid ? meetingCommitteeName(meeting) : null;
  const cancelled = meeting.status === 'cancelled';
  const titleId = useId();
  const answer = asks && !cancelled;
  return (
    <li
      className="flex flex-col transition-colors hover:bg-surface-hover sm:flex-row sm:items-center"
      data-testid={`director-meeting-${meeting.id}`}
    >
      {/* One grid, two readings: at phone width the day follows the title; from `sm` it sits at
          the row's right and the place takes the second line. */}
      <Link
        to={BOARD_DIRECTOR_PAGES.meeting}
        params={{ meetingId: meeting.id }}
        search={search}
        className="group grid min-w-0 flex-1 grid-cols-1 items-baseline gap-x-16 gap-y-4 px-20 py-14 outline-none transition-colors focus-visible:bg-surface-hover sm:grid-cols-[minmax(0,1fr)_auto]"
        data-testid={`director-meeting-link-${meeting.id}`}
      >
        <span
          id={titleId}
          className="min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong transition-colors group-hover:text-brand-700"
        >
          {meeting.title}
        </span>
        <span className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary tabular-nums sm:text-right">
          {meetingWhen(meeting)}
        </span>
        <span className="min-w-0 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          {committee !== null ? (
            <span className="font-w600 text-text-secondary">{committee} · </span>
          ) : null}
          {cancelled ? (
            <StatusWord>{statusLabel(meeting.status)}</StatusWord>
          ) : (
            (meeting.location ?? '')
          )}
        </span>
      </Link>
      {answer ? (
        <div className="flex flex-wrap items-center gap-8 px-20 pb-14 sm:shrink-0 sm:py-14 sm:pl-0">
          {meeting.my_intent ? (
            <>
              <Badge tone="info" testId={`director-meeting-answer-${meeting.id}`}>
                {directorIntentLabel(meeting.my_intent)}
              </Badge>
              {/* CBG-UX-COPY2 (A5f): the answer is a label, so one link says where to change it. */}
              <Link
                to={BOARD_DIRECTOR_PAGES.meeting}
                params={{ meetingId: meeting.id }}
                search={search}
                aria-describedby={titleId}
                className={LINK}
                {...testIdProp(`director-meeting-change-${meeting.id}`)}
              >
                {w.changeAnswer}
              </Link>
            </>
          ) : (
            <Link
              to={BOARD_DIRECTOR_PAGES.meeting}
              params={{ meetingId: meeting.id }}
              search={search}
              aria-describedby={titleId}
              className={SMALL_BUTTON}
              {...testIdProp(`director-meeting-rsvp-${meeting.id}`)}
            >
              {w.canYouMakeIt}
            </Link>
          )}
          {packetId !== null ? (
            <Link
              to={BOARD_DIRECTOR_PAGES.packet}
              params={{ packetId }}
              search={search}
              aria-describedby={titleId}
              className={SMALL_BUTTON}
              {...testIdProp(`director-meeting-packet-${meeting.id}`)}
            >
              {w.openThePacket}
            </Link>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function DirectorMeetingDetailPage() {
  const { meetingId } = useParams({ from: '/director/meetings/$meetingId' });
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);

  const meetingKey = useMemo(
    () => ['board-director-meeting', orgRef, meetingId],
    [orgRef, meetingId],
  );

  const meetingQuery = useQuery({
    queryKey: meetingKey,
    enabled: orgRef !== null && mayFire,
    // A refusal is a deterministic typed 400, not a blip: retrying it just delays the sheet.
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorMeetingOut> => {
      const { data } = await operations.directorGetBoardDirectorMeeting({
        path: { org_ref: orgRef ?? '', meeting_id: meetingId },
        throwOnError: true,
      });
      return data;
    },
  });

  // The reader's clock, re-read at the meeting's start, so the RSVP goes the moment it begins.
  const now = useMinuteClock(meetingQuery.data?.starts_at ?? null);
  // CBG-FIX34: a request the board refuses this account re-reads `/me` as it says so.
  const failureSentence = useDirectorFailureSentence();

  const calendar = useMutation({
    mutationFn: async () => {
      const { data } = await operations.directorGetBoardDirectorMeetingCalendar({
        path: { org_ref: orgRef ?? '', meeting_id: meetingId },
        throwOnError: true,
      });
      saveCalendarFile(data, meetingQuery.data?.title ?? 'Board meeting');
    },
    onError: (caught) =>
      toast.error(
        failureSentence(caught, 'We couldn’t prepare that calendar file. Please try again.'),
      ),
  });

  const rsvp = useMutation({
    mutationFn: async (intent: BoardAttendanceIntentValue) => {
      await operations.directorRecordBoardDirectorAttendanceIntent({
        path: { org_ref: orgRef ?? '', meeting_id: meetingId },
        body: { intent },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meetingKey });
      await queryClient.invalidateQueries({ queryKey: directorMeetingsKey(orgRef) });
      toast.success('Thanks — your answer is recorded.');
    },
    onError: (caught) => {
      toast.error(failureSentence(caught, 'We couldn’t record that. Please try again.'));
    },
  });

  const meeting = meetingQuery.data ?? null;
  const started = meeting !== null && meetingHasStarted(meeting.starts_at, now);
  const identity = directorBoardIdentity(board);
  // CBG-CAL1: the director's meeting line — this seat was sent the meeting (from an address, or by
  // email from Givmo), so the calendar file, which would add a second entry, gives way to it. Null
  // while both calendar flags are off and for any seat no invitation reached: the file stays.
  const sentTo = decodeDirectorMeetingCalendar(
    (meeting as { calendar?: unknown } | null)?.calendar,
  );
  const back = (
    <Link
      to={BOARD_DIRECTOR_PAGES.meetings}
      search={search}
      // Exact: this page sits beneath that address, and it is not the current page.
      exact
      className={LINK}
      {...testIdProp('director-meeting-back')}
    >
      All meetings
    </Link>
  );

  if (
    orgRef === null ||
    notYours ||
    meetingQuery.isPending ||
    meetingQuery.isError ||
    meeting === null
  ) {
    return (
      <DirectorShell
        title="Meeting"
        identity={identity}
        // CBG-FIX34 (review-134's M1): no way into a board this account no longer holds.
        actions={notYours ? undefined : back}
        testId="page-director-meeting-detail"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {meetingQuery.isPending ? (
            <Skeleton className="h-90 w-full rounded-lg" testId="director-meeting-loading" />
          ) : null}
          {/* ⚠ A refusal must NOT land in the error card below: the read gate opens the sheet and
              re-reads THIS meeting once the window is open. */}
          <DirectorReadRefusal reads={[meetingQuery]} testId="director-meeting-read-step-up" />
          {meetingQuery.isError && !isSigningRefusal(meetingQuery.error) ? (
            <DirectorReadFailed
              read={meetingQuery}
              line="We couldn’t open this meeting."
              testId="director-meeting-error"
              retryTestId="director-meeting-retry"
            />
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  return (
    <DirectorShell
      title={meeting.title}
      description={
        // CBG-YR6-MEET (not drawn): a committee's meeting names its committee first, as the officer's
        // page does (frame c5).
        meetingCommitteeName(meeting) !== null
          ? `${meetingCommitteeName(meeting)} · ${displayMeetingStart(meeting)}`
          : displayMeetingStart(meeting)
      }
      identity={identity}
      actions={back}
      testId="page-director-meeting-detail"
    >
      <Card testId="director-meeting-summary">
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center gap-8">
            <Badge tone="neutral">{meetingTypeLabel(meeting.meeting_type)}</Badge>
            {!started || meeting.status === 'cancelled' ? (
              <Badge tone={statusTone(meeting.status)}>{statusLabel(meeting.status)}</Badge>
            ) : null}
          </div>

          {meeting.location ? (
            <p className="font-sans text-s15 font-w400 tracking-givmo text-text-secondary">
              {meeting.location}
            </p>
          ) : null}

          {meeting.video_link ? (
            <a
              href={meeting.video_link}
              target="_blank"
              rel="noreferrer"
              className="self-start font-sans text-s15 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="director-meeting-video-link"
            >
              Join link
            </a>
          ) : null}

          {meeting.status === 'cancelled' && meeting.cancellation_reason ? (
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Cancelled: {meeting.cancellation_reason}
            </p>
          ) : null}

          <NoticeBlock
            notice={meeting.notice}
            committee={meetingCommitteeName(meeting)}
            testId="director-meeting-notice"
          />
        </CardBody>
      </Card>

      {/* CBG-FIX145: a held meeting whose own minutes are the record leads to them, first: once the
          board has approved them they are what a director comes back for, before the papers it was
          sent. The backend names them (`minutes_record_id`) only once they are approved and only where
          this director may read them by the record read's own rule, so a draft is never offered as
          "the minutes" and nothing here decides who may read what. */}
      {meeting.minutes_record_id ? (
        <Card testId="director-meeting-minutes">
          <CardBody className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex min-w-0 flex-col gap-4">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {w.meetingMinutesTitle}
              </span>
              <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {w.meetingMinutesLine}
              </span>
            </div>
            <Link
              to={BOARD_DIRECTOR_PAGES.record}
              params={{ recordId: meeting.minutes_record_id }}
              search={search}
              className={LINK}
              {...testIdProp('director-meeting-minutes-link')}
            >
              {w.readTheMinutes}
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* The papers come FIRST, above the RSVP: reading the packet is what a director opens this for,
          and `published_packet_id` is absent until a version has actually gone out — a director is
          never pointed at work in progress. */}
      {meeting.published_packet_id ? (
        <Card testId="director-meeting-packet">
          <CardBody className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex min-w-0 flex-col gap-4">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                Your papers for this meeting
              </span>
              <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {/* CBG-FIX90 (frame o9): a committee's meeting sent its papers to the committee. */}
                {meetingCommitteeName(meeting) !== null
                  ? co.papersLine
                  : 'Everything the board was sent, plus somewhere to keep your own notes.'}
              </span>
            </div>
            <Link
              to={BOARD_DIRECTOR_PAGES.packet}
              params={{ packetId: meeting.published_packet_id }}
              search={search}
              className={LINK}
              {...testIdProp('director-meeting-packet-link')}
            >
              Open the packet
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* Below the packet and above the RSVP: after a meeting is held, what was recorded about it is
          what a director comes back for, and anything waiting on their signature leads that.
          CBG-FIX91 (frame o9): only once the meeting has started — nothing can have been recorded
          before then, so its "Nothing recorded here yet." would offer nothing (and saying so reveals
          nothing: no session of it exists yet). */}
      {started ? (
        <DirectorMinutesPanel
          orgRef={orgRef}
          meetingId={meetingId}
          committee={meetingCommitteeName(meeting)}
        />
      ) : null}

      {meeting.status !== 'cancelled' && !started ? (
        <Card testId="director-rsvp">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Can you make it?
            </h2>
            {/* The calendar file sits with the RSVP because they answer the same question. It is
                fetched on demand and never cached: the backend regenerates it per request, and the
                higher sequence number in the regenerated file is what makes a calendar application
                UPDATE its entry for a rescheduled meeting instead of adding a second one. */}
            {sentTo !== null ? (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="director-meeting-sent-to-calendar"
              >
                {sentTo.sent_from === 'givmo'
                  ? calendarWords.directorSentGivmo(board?.organization_name?.trim() ?? '')
                  : calendarWords.directorSentFrom(sentTo.sent_from)}
              </p>
            ) : (
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                className="self-start"
                loading={calendar.isPending}
                onClick={() => calendar.mutate()}
                testId="director-meeting-calendar"
              >
                Add it to your calendar
              </Button>
            )}
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {/* CBG-FIX90 (frame o9): a committee's meeting tells the committee. */}
              {meetingCommitteeName(meeting) !== null
                ? co.rsvpLine
                : 'This just lets the board know what to expect. You can change your answer any time, and it isn’t a record of who actually attended.'}
            </p>
            <div className="flex flex-wrap gap-10">
              {RSVP_CHOICES.map((choice) => (
                <Button
                  key={choice.value}
                  appearance="saas"
                  tone={meeting.my_intent === choice.value ? 'primary' : 'secondary'}
                  disabled={rsvp.isPending}
                  onClick={() => rsvp.mutate(choice.value)}
                  testId={`director-rsvp-${choice.value}`}
                >
                  {choice.label}
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}

      {(meeting.agenda ?? []).length > 0 ? (
        <Card testId="director-agenda">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">Agenda</h2>
            <ol className="flex flex-col gap-8">
              {(meeting.agenda ?? []).map((item, index) => (
                <li
                  key={item.id}
                  className="flex gap-12"
                  data-testid={`director-agenda-${item.id}`}
                >
                  <span className="w-20 shrink-0 text-center font-sans text-s13 font-w600 tracking-givmo text-text-tertiary">
                    {index + 1}
                  </span>
                  <span className="flex min-w-0 flex-col gap-2">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {item.title}
                    </span>
                    {item.description ? (
                      <span className="whitespace-pre-line font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                        {keepRangesTogether(item.description)}
                      </span>
                    ) : null}
                    {item.is_consent_agenda ? (
                      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                        On the consent agenda
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      ) : null}
    </DirectorShell>
  );
}
